import React, { useEffect, useState, useMemo } from 'react';
import { usePaymentStore, useAuthStore } from '@/store';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  CheckCircle,
  Search,
  Banknote,
  CreditCard,
  Building2,
  Receipt,
  CalendarDays,
  Trash2,
  Filter,
  TrendingUp,
  Download,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getAuthAxios } from '@/store';
import { useI18n } from '@/components/I18nProvider';

const PAYMENT_METHODS = ['Tümü', 'Nakit', 'EFT/Havale', 'Kredi Kartı', 'Vadeli Çek'];

const methodIcons = {
  'Nakit': Banknote,
  'EFT/Havale': Building2,
  'Kredi Kartı': CreditCard,
  'Vadeli Çek': Receipt,
};

const formatCurrency = (amount, currency = 'TRY') => {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(amount);
};

export default function CollectedPage() {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { payments, isLoading, fetchPayments } = usePaymentStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [methodFilter, setMethodFilter] = useState('Tümü');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const collectedPayments = useMemo(() => {
    return payments
      .filter((p) => p.status === 'paid')
      .filter((p) => {
        if (searchTerm) {
          const q = searchTerm.toLowerCase();
          if (!p.cari_title?.toLowerCase().includes(q) && !p.note?.toLowerCase().includes(q)) return false;
        }
        if (methodFilter !== 'Tümü' && p.payment_method !== methodFilter) return false;
        if (dateFrom) {
          const paidDate = (p.paid_at || p.updated_at || '').split('T')[0];
          if (paidDate < dateFrom) return false;
        }
        if (dateTo) {
          const paidDate = (p.paid_at || p.updated_at || '').split('T')[0];
          if (paidDate > dateTo) return false;
        }
        return true;
      })
      .sort((a, b) => new Date(b.paid_at || b.updated_at) - new Date(a.paid_at || a.updated_at));
  }, [payments, searchTerm, methodFilter, dateFrom, dateTo]);

  // Stats by method
  const stats = useMemo(() => {
    const total = collectedPayments.reduce((sum, p) => sum + p.amount, 0);
    const byMethod = {};
    collectedPayments.forEach((p) => {
      const m = p.payment_method || 'Belirtilmemiş';
      if (!byMethod[m]) byMethod[m] = { count: 0, total: 0 };
      byMethod[m].count++;
      byMethod[m].total += p.amount;
    });
    return { total, count: collectedPayments.length, byMethod };
  }, [collectedPayments]);

  const handleUnmark = async (paymentId) => {
    if (!window.confirm('Bu tahsilatı geri almak istediğinize emin misiniz?')) return;
    try {
      await getAuthAxios().post(`/payments/${paymentId}/unmark-paid`);
      toast.success('Tahsilat geri alındı');
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem hatası');
    }
  };

  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';
  const canDelete = true; // All roles can delete their own data

  const exportToCSV = () => {
    if (collectedPayments.length === 0) return toast.error(t('exportNoData'));
    const header = 'Müşteri,Telefon,Tutar,Para Birimi,Ödeme Yöntemi,Tahsil Tarihi,Vade Tarihi,Not\n';
    const rows = collectedPayments.map((p) =>
      `"${p.cari_title || ''}","${p.cari_phone || ''}",${p.amount},"${p.currency}","${p.payment_method || ''}","${p.paid_at ? format(new Date(p.paid_at), 'dd.MM.yyyy') : ''}","${format(new Date(p.due_date), 'dd.MM.yyyy')}","${(p.note || '').replace(/"/g, '""')}"`
    ).join('\n');
    const blob = new Blob(['\uFEFF' + header + rows], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'tahsil-edilenler.csv';
    link.click();
    toast.success('Dışa aktarıldı');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="collected-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold">Tahsil Edilenler</h1>
          <p className="text-sm text-muted-foreground">Tahsil edilen ödemeleri takip edin</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportToCSV} data-testid="export-collected-btn">
          <Download className="mr-2 h-4 w-4" />
          Dışa Aktar
        </Button>
      </div>

      {/* Dashboard Stats */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4" data-testid="collected-stats">
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2">
              <div className="shrink-0 rounded-lg bg-green-100 p-2"><TrendingUp className="h-4 w-4 text-green-600" /></div>
              <div className="min-w-0">
                <p className="text-[11px] sm:text-xs text-muted-foreground truncate">Toplam Tahsilat</p>
                <p className="font-heading text-sm sm:text-lg font-bold truncate">{formatCurrency(stats.total)}</p>
                <p className="text-[10px] text-muted-foreground">{stats.count} adet</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {Object.entries(stats.byMethod).map(([method, data]) => {
          const Icon = methodIcons[method] || Receipt;
          return (
            <Card key={method}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2">
                  <div className="shrink-0 rounded-lg bg-primary/10 p-2"><Icon className="h-4 w-4 text-primary" /></div>
                  <div className="min-w-0">
                    <p className="text-[11px] sm:text-xs text-muted-foreground truncate">{method}</p>
                    <p className="font-heading text-sm sm:text-lg font-bold truncate">{formatCurrency(data.total)}</p>
                    <p className="text-[10px] text-muted-foreground">{data.count} adet</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder={t('search')} className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="collected-search" />
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <Select value={methodFilter} onValueChange={setMethodFilter}>
                <SelectTrigger className="w-[140px] text-xs" data-testid="method-filter">
                  <Filter className="h-3.5 w-3.5 mr-1" /><SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[130px] text-xs" data-testid="collected-date-from" />
              <span className="text-xs text-muted-foreground">-</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[130px] text-xs" data-testid="collected-date-to" />
              {(dateFrom || dateTo || methodFilter !== 'Tümü') && (
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setDateFrom(''); setDateTo(''); setMethodFilter('Tümü'); }}>Temizle</Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : collectedPayments.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <CheckCircle className="mx-auto mb-3 h-10 w-10 opacity-30" />
          <p>Henüz tahsil edilen ödeme yok</p>
        </CardContent></Card>
      ) : (
        <>
          {/* Mobile */}
          <div className="space-y-2 md:hidden">
            {collectedPayments.map((p) => (
              <Card key={p.id} className="bg-green-50/30 border-green-200/50" data-testid={`collected-card-${p.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm truncate">{p.cari_title}</span>
                        {p.payment_method && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0">{p.payment_method}</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Tahsil: {p.paid_at ? format(new Date(p.paid_at), 'dd.MM.yyyy') : '-'} | Vade: {format(new Date(p.due_date), 'dd.MM.yyyy')}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="font-bold text-sm text-green-700">{formatCurrency(p.amount, p.currency)}</span>
                      {canDelete && (
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleUnmark(p.id)} data-testid={`delete-collected-${p.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop */}
          <Card className="hidden md:block">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Müşteri</TableHead>
                    <TableHead className="text-right">{t('amount')}</TableHead>
                    <TableHead>Yöntem</TableHead>
                    <TableHead>Tahsil Tarihi</TableHead>
                    <TableHead>{t('dueDate')}</TableHead>
                    <TableHead className="hidden lg:table-cell">Not</TableHead>
                    {canDelete && <TableHead className="text-right">İşlem</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {collectedPayments.map((p) => (
                    <TableRow key={p.id} data-testid={`collected-row-${p.id}`}>
                      <TableCell>
                        <div className="font-medium">{p.cari_title}</div>
                        <div className="text-xs text-muted-foreground">{p.cari_phone}</div>
                      </TableCell>
                      <TableCell className="text-right font-medium whitespace-nowrap text-green-700">
                        {formatCurrency(p.amount, p.currency)}
                      </TableCell>
                      <TableCell>
                        {p.payment_method ? (
                          <Badge variant="outline" className="text-xs">{p.payment_method}</Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{p.paid_at ? format(new Date(p.paid_at), 'dd.MM.yyyy') : '-'}</TableCell>
                      <TableCell>{format(new Date(p.due_date), 'dd.MM.yyyy')}</TableCell>
                      <TableCell className="hidden lg:table-cell max-w-[200px] truncate">{p.note || '-'}</TableCell>
                      {canDelete && (
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => handleUnmark(p.id)} title="Tahsilatı Geri Al" data-testid={`delete-collected-${p.id}`}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
